import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  ChallengeResult,
  MfaAction,
  MFA_CHALLENGE_EXPIRED,
  MFA_OTP_INVALID,
  MFA_PHONE_MISSING,
  VerifyResult,
  mfaErrorCode
} from './mfa.types';

const API = `${environment.apiBaseUrl}/api/mfa`;

interface ApiResponse<T> {
  success: boolean;
  data:    T | null;
  message: string | null;
  errors:  string[] | null;
}

const CHALLENGE_FALLBACK     = 'Could not send a verification code. Please try again.';
const VERIFY_FALLBACK        = 'Could not verify the code. Please try again.';
const PHONE_MISSING_FALLBACK = 'Your account has no registered phone number. Add one to continue.';
const EXPIRED_FALLBACK       = 'Your code has expired. Request a new one.';
const LOCKED_FALLBACK        = 'Too many incorrect attempts. Request a new code.';
const INCORRECT_FALLBACK     = 'Incorrect code. Please try again.';

/**
 * HTTP for the two-step MFA challenge. The `X-Active-Context` and `Bearer`
 * headers are attached upstream by the auth interceptor — these endpoints are
 * not public. Errors are mapped to discriminated unions so the modal/coordinator
 * never touch HTTP status codes (mirrors PhoneVerificationService).
 */
@Injectable({ providedIn: 'root' })
export class MfaService {
  private readonly http = inject(HttpClient);

  /** Step 1 — request an OTP for `action`. */
  challenge(action: MfaAction): Observable<ChallengeResult> {
    return this.http
      .post<ApiResponse<{ challengeId: string }>>(`${API}/challenge`, { action })
      .pipe(
        map(env => (env?.success && env.data?.challengeId
          ? { kind: 'sent', challengeId: env.data.challengeId }
          : { kind: 'unavailable', message: env?.message?.trim() || CHALLENGE_FALLBACK }) as ChallengeResult),
        catchError((err: unknown) => of(this.challengeError(err)))
      );
  }

  /** Step 2 — submit the OTP, returning the single-use mfaToken on success. */
  verify(challengeId: string, otp: string): Observable<VerifyResult> {
    return this.http
      .post<ApiResponse<{ mfaToken: string }>>(`${API}/challenge/${challengeId}/verify`, { otp })
      .pipe(
        map(env => (env?.success && env.data?.mfaToken
          ? { kind: 'verified', mfaToken: env.data.mfaToken }
          : { kind: 'unavailable', message: env?.message?.trim() || VERIFY_FALLBACK }) as VerifyResult),
        catchError((err: unknown) => of(this.verifyError(err)))
      );
  }

  // ---- error mapping -------------------------------------------------------

  private challengeError(err: unknown): ChallengeResult {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as ApiResponse<unknown> | null;
      const message = body?.message?.trim() || '';
      if (mfaErrorCode(err) === MFA_PHONE_MISSING || err.status === 422) {
        return { kind: 'phone-missing', message: message || PHONE_MISSING_FALLBACK };
      }
      return { kind: 'unavailable', message: message || CHALLENGE_FALLBACK };
    }
    return { kind: 'unavailable', message: CHALLENGE_FALLBACK };
  }

  private verifyError(err: unknown): VerifyResult {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as ApiResponse<unknown> | null;
      const message = body?.message?.trim() || '';
      const code = mfaErrorCode(err);
      if (code === MFA_CHALLENGE_EXPIRED) {
        return { kind: 'expired', message: message || EXPIRED_FALLBACK };
      }
      if (code === MFA_OTP_INVALID || err.status === 400) {
        // The challenge locks after 5 wrong attempts — the server signals this
        // via the message text (we don't track the count client-side).
        if (/lock|too many/i.test(message)) {
          return { kind: 'locked', message: message || LOCKED_FALLBACK };
        }
        return { kind: 'incorrect', message: message || INCORRECT_FALLBACK };
      }
      return { kind: 'unavailable', message: message || VERIFY_FALLBACK };
    }
    return { kind: 'unavailable', message: VERIFY_FALLBACK };
  }
}
